/**
 * useEnv Hook
 * Provides clean access to environment configuration from React components
 *
 * This is now a thin wrapper around @perfectserve/env-ps, which handles
 * configuration from multiple sources (Vite build-time, Docker runtime, etc.)
 */

import { useEffect, useState } from "react";

import env, {
  getApiUrl as _buildApiUrl,
  getConfig as _getConfig,
  isDevelopment as _checkDev,
  isFeatureEnabled as _checkFeature,
  isProduction as _checkProd,
  type PSEnvConfig as _PSEnvConfig,
  type PSEnvConfig,
} from "@perfectserve/env-ps";

/**
 * Synchronous environment configuration access
 * Used for early initialization (like Sentry) before React components mount
 *
 * Now delegated to @perfectserve/env-ps package
 */
export const getEnvConfig = (): PSConfig | null => {
  // Convert PSEnvConfig to legacy PSConfig format for backwards compatibility
  return {
    sentryDsn: env.sentryDsn || "",
    env: env.environment || "",
    environment: env.environment || "",
    path: "src",
    systemApi: env.systemApi || "",
    attachmentApi: env.attachmentApi || "",
    directoryApi: env.directoryApi || "",
    directorySearchApi: env.directorySearchApi || "",
    gatewayApi: env.gatewayApi || "",
    cspApi: env.cspApi || "",
    oidc: {
      accessTokenExpiringNotificationTime: env.oidc?.tokenNotificationExpireTime || 60,
      authority: env.oidc?.authority || "",
      checkSessionInterval: env.oidc?.checkSessionInterval || 2000,
      client_id: env.oidc?.clientId || "",
      clock_skew: env.oidc?.clockSkew || 300,
      post_logout_redirect_uri: env.oidc?.postLogoutRedirect || "",
      redirect_uri: env.oidc?.redirectUri || "",
      response_type: env.oidc?.responseType || "code",
      scope: env.oidc?.scope || "openid profile",
      silent_redirect_uri: env.oidc?.silentRedirectUri || "",
      silentRequestTimeout: env.oidc?.silentRequestTimeout || 10000,
      log: env.oidc?.log || false,
    },
    features: {
      enablePendoAnalytics: env.features?.pendoAnalytics || false,
      enableSentryLogging: env.features?.sentryLogging || false,
      debugMode: env.features?.debugMode || false,
    },
    version: env.build?.number || "dev",
    buildId: env.build?.number || "unknown",
  };
};

/**
 * Get a specific environment configuration value
 * @param key - The configuration key to retrieve
 * @returns The configuration value or undefined if not found
 */
export const getEnvValue = <K extends keyof PSConfig>(key: K): PSConfig[K] | undefined => {
  const config = getEnvConfig();
  return config?.[key];
};

// TypeScript interface for the environment configuration (legacy format)
export interface PSConfig {
  // Core App Info
  appName?: string;
  path?: string;
  env?: string;
  domain?: string;

  // API Endpoints
  apiBaseUrl?: string;
  systemApi?: string;
  directoryApi?: string;
  directorySearchApi?: string;
  privilegeApi?: string;
  practitionerApi?: string;
  gatewayApi?: string;
  cspApi?: string;
  attachmentApi?: string;

  // Third-party Services
  sentryDsn?: string;

  // OIDC Configuration
  oidc?: PSOIDC;

  // Feature Flags
  features?: {
    enablePendoAnalytics?: boolean;
    enableSentryLogging?: boolean;
    debugMode?: boolean;
  };

  // Environment Info
  environment?: string;
  version?: string;
  buildId?: string;
}

export interface PSOIDC {
  authority: string;
  client_id: string;
  redirect_uri: string;
  post_logout_redirect_uri: string;
  response_type: string;
  scope: string;
  silent_redirect_uri: string;
  accessTokenExpiringNotificationTime?: number;
  checkSessionInterval?: number;
  clock_skew?: number;
  silentRequestTimeout?: number;
  log?: boolean;
}

// Global window interface extension
declare global {
  interface Window {
    PSCONFIG?: PSEnvConfig;
    PS_HTTP_SERVICE?: any; // Global HTTP service for packages like Context Selector
  }
}

/**
 * Custom hook to access environment configuration
 * Loads configuration once on mount
 */
export const useEnv = () => {
  const [config, setConfig] = useState<PSConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConfig = () => {
      try {
        // Use getEnvConfig() which now delegates to @perfectserve/env-ps
        const envConfig = getEnvConfig();

        if (!envConfig) {
          throw new Error("No environment configuration found");
        }

        setConfig(envConfig);
        setError(null);
      } catch (err) {
        const errorMessage = `Failed to load environment config: ${err instanceof Error ? err.message : "Unknown error"}`;
        // Silently handle environment config errors
        setError(errorMessage);
        setConfig(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []); // Only run once on mount

  // Helper functions for easy access to common config values
  const getApiUrl = (endpoint: string): string => {
    if (!config) return "";
    return (config.apiBaseUrl || "") + endpoint;
  };

  const isFeatureEnabled = (feature: keyof NonNullable<PSConfig["features"]>): boolean => {
    return config?.features?.[feature] || false;
  };

  const isDevelopment = (): boolean => {
    return config?.environment === "development" || config?.environment === "local";
  };

  const isProduction = (): boolean => {
    return config?.environment === "production";
  };

  // Return hook interface
  return {
    // Core data
    config,
    isLoading,
    error,

    // Convenience getters
    apiBaseUrl: config?.apiBaseUrl || "",
    systemApi: config?.systemApi || "",
    directoryApi: config?.directoryApi || "",
    directorySearchApi: config?.directorySearchApi || "",
    privilegeApi: config?.privilegeApi || "",
    practitionerApi: config?.practitionerApi || "",
    gatewayApi: config?.gatewayApi || "",
    cspApi: config?.cspApi || "",
    attachmentApi: config?.attachmentApi || "",
    sentryDsn: config?.sentryDsn,
    oidcConfig: config?.oidc,
    environment: config?.environment || "unknown",
    version: config?.version || "0.0.0",

    // Helper functions
    getApiUrl,
    isFeatureEnabled,
    isDevelopment,
    isProduction,

    // Raw access
    getRawConfig: () => config,
  };
};

// Export default for easy importing
export default useEnv;

/**
 * Example usage:
 *
 * // For early initialization (like Sentry):
 * import { getEnvValue } from './hooks/useEnv';
 * const sentryDsn = getEnvValue('sentryDsn');
 *
 * // In React components:
 * import { useEnv } from './hooks/useEnv';
 * const { sentryDsn, systemApi, isLoading } = useEnv();
 *
 * // Or with the default export:
 * import useEnv from './hooks/useEnv';
 * const env = useEnv();
 * console.info(env.sentryDsn);
 *
 * // For direct access to the new unified config:
 * import env from '@perfectserve/env-ps';
 * console.info(env.api.system, env.features.pendoAnalytics);
 */
